import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import {
  ShieldCheck, ArrowRight, Plus, ExternalLink, Users, Upload, Sparkles, Send,
  CalendarClock, Link as LinkIcon, Trash2, AlertTriangle,
} from "lucide-react";

const POLICY_TYPES = ["Health", "Term", "Life", "Motor", "Other"];
const RENEWAL_STATES = ["None", "Quote Prepared", "Renewal Email Sent", "Renewed", "Lapsed"];
const STATUS_TONE = {
  "None": "bg-gray-100 text-gray-700 border-gray-200",
  "Quote Prepared": "bg-amber-50 text-amber-700 border-amber-200",
  "Renewal Email Sent": "bg-blue-50 text-blue-700 border-blue-200",
  "Renewed": "bg-emerald-50 text-emerald-700 border-emerald-200",
  "Lapsed": "bg-rose-50 text-rose-700 border-rose-200",
};

const fmtDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" });
};

const fmtCurrency = (v) => (typeof v === "number" ? `₹${v.toLocaleString("en-IN")}` : "—");

const daysUntil = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return Math.ceil((d - new Date()) / (1000 * 60 * 60 * 24));
};

export default function InsuranceTab({ client, canEdit }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [policies, setPolicies] = useState([]);
  const [refs, setRefs] = useState([]);
  const [canAct, setCanAct] = useState(false);
  const [editId, setEditId] = useState(null); // policy id for detail dialog
  const [newOpen, setNewOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/clients/${client.id}/insurance-policies`);
      setPolicies(data.policies || []);
      setRefs(data.floater_references || []);
      setCanAct(!!data.can_act && !!canEdit);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load insurance policies.");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [client.id]);

  return (
    <div className="space-y-6" data-testid="insurance-tab">
      {canAct && (
        <Card className="border-l-4 border-l-primary bg-white p-4" data-testid="policy-analyzer-hint">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-start gap-3">
              <ShieldCheck className="mt-0.5 h-4 w-4 text-primary" />
              <div>
                <div className="font-dashboard text-sm font-bold">Have the policy document?</div>
                <p className="text-xs text-muted-foreground">
                  Upload it once — the AI reads the whole policy and drafts the details for your review.
                </p>
              </div>
            </div>
            <Button size="sm" variant="outline" className="gap-1"
                    onClick={() => navigate(`/app/clients/${client.id}?tab=tools`)}
                    data-testid="open-policy-analyzer-btn">
              Open Analyzer <ArrowRight className="h-3 w-3" />
            </Button>
          </div>
        </Card>
      )}

      <Card className="border-border p-5">
        <div className="mb-3 flex items-center justify-between">
          <div>
            <h3 className="font-dashboard text-base font-bold">Policies on record</h3>
            <p className="mt-0.5 text-xs text-muted-foreground">
              Every policy in force for this client. Floater policies are entered once on the proposer's profile.
            </p>
          </div>
          {canAct && (
            <Button size="sm" onClick={() => setNewOpen(true)} className="gap-1" data-testid="add-policy-btn">
              <Plus className="h-4 w-4" /> Add policy
            </Button>
          )}
        </div>
        {loading ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : policies.length === 0 ? (
          <p className="text-sm text-muted-foreground" data-testid="insurance-empty">No policies on record yet.</p>
        ) : (
          <div className="grid gap-3 md:grid-cols-2">
            {policies.map((p) => (
              <PolicyCard key={p.id} policy={p} onOpen={() => setEditId(p.id)} />
            ))}
          </div>
        )}
      </Card>

      {refs.length > 0 && (
        <Card className="border-border p-5" data-testid="floater-refs-card">
          <h3 className="mb-2 flex items-center gap-2 font-dashboard text-sm font-bold">
            <Users className="h-4 w-4 text-primary" /> Covered under another member's floater
          </h3>
          <p className="mb-3 text-xs text-muted-foreground">
            Full policy details live under the proposer's profile — no duplicate entry needed here.
          </p>
          <ul className="space-y-2">
            {refs.map((r) => (
              <li key={r.id} className="flex items-center justify-between gap-3 rounded-md border border-border bg-muted/30 px-3 py-2 text-sm"
                  data-testid={`floater-ref-${r.id}`}>
                <div className="min-w-0">
                  <div className="font-medium">
                    Covered via <b>{r.proposer_name}</b>'s floater
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {r.insurer} · {r.policy_type} · {fmtCurrency(r.sum_insured)} · Expires {fmtDate(r.expiry_date)}
                  </div>
                </div>
                <Button size="sm" variant="outline" className="gap-1"
                        onClick={() => navigate(`/app/clients/${r.proposer_client_id}?tab=insurance`)}
                        data-testid={`floater-ref-open-${r.id}`}>
                  Open <ArrowRight className="h-3 w-3" />
                </Button>
              </li>
            ))}
          </ul>
        </Card>
      )}

      <PolicyDialog
        open={newOpen}
        onOpenChange={setNewOpen}
        clientId={client.id}
        onSaved={() => { setNewOpen(false); load(); }}
      />
      <PolicyDetailDialog
        open={!!editId}
        onOpenChange={(v) => { if (!v) setEditId(null); }}
        clientId={client.id}
        policyId={editId}
        canAct={canAct}
        onChanged={load}
      />
    </div>
  );
}

// ----------------------------------------------------------------------
// Compact policy card for the grid.
// ----------------------------------------------------------------------
function PolicyCard({ policy, onOpen }) {
  const dLeft = daysUntil(policy.expiry_date);
  const expiring = dLeft !== null && dLeft <= 60 && dLeft >= 0;
  const rs = policy.renewal_status || "None";
  return (
    <button onClick={onOpen}
            className="group relative rounded-md border border-border bg-white p-4 text-left transition hover:border-primary hover:shadow-sm"
            data-testid={`policy-card-${policy.id}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <Badge variant="secondary" className="text-[10px]">{policy.policy_type}</Badge>
            {policy.is_floater && <Badge variant="secondary" className="gap-1 text-[10px]"><Users className="h-3 w-3" /> Floater</Badge>}
            {rs !== "None" && (
              <span className={"rounded-full border px-2 py-0.5 text-[10px] " + STATUS_TONE[rs]}>{rs}</span>
            )}
          </div>
          <div className="mt-1 font-dashboard text-sm font-bold truncate" title={policy.insurer || ""}>
            {policy.insurer || "—"}
          </div>
          <div className="text-xs text-muted-foreground truncate">
            {policy.policy_number ? `#${policy.policy_number}` : "no policy #"}
          </div>
        </div>
        <ArrowRight className="h-4 w-4 shrink-0 text-muted-foreground opacity-0 transition group-hover:opacity-100" />
      </div>

      <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Sum insured</div>
          <div className="font-semibold">{fmtCurrency(policy.sum_insured)}</div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Premium</div>
          <div className="font-semibold">{fmtCurrency(policy.premium)}{policy.premium_frequency ? ` / ${policy.premium_frequency}` : ""}</div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Start</div>
          <div className="font-medium">{fmtDate(policy.start_date)}</div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Expiry</div>
          <div className={"font-medium " + (expiring ? "text-amber-700" : "")}>
            {fmtDate(policy.expiry_date)}
            {expiring && <span className="ml-1 text-[10px]">({dLeft}d)</span>}
          </div>
        </div>
      </div>

      {policy.proton_drive_link && (
        <div className="mt-3 flex items-center gap-1 text-[11px] text-primary">
          <ExternalLink className="h-3 w-3" /> Proton Drive link attached
        </div>
      )}
    </button>
  );
}

// ----------------------------------------------------------------------
// New-policy dialog. Minimal form — detail editing happens in PolicyDetailDialog.
// ----------------------------------------------------------------------
function PolicyDialog({ open, onOpenChange, clientId, onSaved }) {
  const [form, setForm] = useState(blankForm());
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open) setForm(blankForm()); }, [open]);

  const save = async () => {
    if (!form.insurer.trim()) { toast.error("Insurer is required."); return; }
    setSaving(true);
    try {
      await api.post(`/clients/${clientId}/insurance-policies`, {
        ...form,
        sum_insured: form.sum_insured === "" ? null : Number(form.sum_insured),
        premium: form.premium === "" ? null : Number(form.premium),
      });
      toast.success("Policy added.");
      onSaved && onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save.");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl" data-testid="policy-dialog">
        <DialogHeader>
          <DialogTitle>Add insurance policy</DialogTitle>
          <DialogDescription>
            Enter the key details. You can attach the Proton Drive link and floater members from the detail view after saving.
          </DialogDescription>
        </DialogHeader>
        <PolicyFormFields form={form} setForm={setForm} />
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="policy-save-btn">
            {saving ? "Saving…" : "Save policy"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function blankForm() {
  return {
    policy_type: "Health", insurer: "", policy_number: "",
    sum_insured: "", premium: "", premium_frequency: "Annual",
    start_date: "", expiry_date: "",
    is_floater: false, floater_covered_members: [],
    proton_drive_link: "", member_name: "",
  };
}

function PolicyFormFields({ form, setForm }) {
  const upd = (k, v) => setForm({ ...form, [k]: v });
  return (
    <div className="grid grid-cols-2 gap-3">
      <div>
        <label className="mb-1 block text-xs font-semibold text-muted-foreground">Type</label>
        <select value={form.policy_type} onChange={(e) => upd("policy_type", e.target.value)}
                className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                data-testid="policy-type-select">
          {POLICY_TYPES.map((t) => <option key={t}>{t}</option>)}
        </select>
      </div>
      <div>
        <label className="mb-1 block text-xs font-semibold text-muted-foreground">Insurer</label>
        <Input value={form.insurer} onChange={(e) => upd("insurer", e.target.value)}
               data-testid="policy-insurer" />
      </div>
      <div>
        <label className="mb-1 block text-xs font-semibold text-muted-foreground">Policy number</label>
        <Input value={form.policy_number} onChange={(e) => upd("policy_number", e.target.value)} data-testid="policy-number" />
      </div>
      <div>
        <label className="mb-1 block text-xs font-semibold text-muted-foreground">Member name</label>
        <Input value={form.member_name} onChange={(e) => upd("member_name", e.target.value)} />
      </div>
      <div>
        <label className="mb-1 block text-xs font-semibold text-muted-foreground">Sum insured (₹)</label>
        <Input type="number" value={form.sum_insured} onChange={(e) => upd("sum_insured", e.target.value)} data-testid="policy-sum" />
      </div>
      <div>
        <label className="mb-1 block text-xs font-semibold text-muted-foreground">Premium (₹)</label>
        <Input type="number" value={form.premium} onChange={(e) => upd("premium", e.target.value)} />
      </div>
      <div>
        <label className="mb-1 block text-xs font-semibold text-muted-foreground">Start date</label>
        <Input type="date" value={form.start_date} onChange={(e) => upd("start_date", e.target.value)} data-testid="policy-start" />
      </div>
      <div>
        <label className="mb-1 block text-xs font-semibold text-muted-foreground">Expiry date</label>
        <Input type="date" value={form.expiry_date} onChange={(e) => upd("expiry_date", e.target.value)} data-testid="policy-expiry" />
      </div>
      <div className="col-span-2">
        <label className="mb-1 block text-xs font-semibold text-muted-foreground">Proton Drive link</label>
        <Input value={form.proton_drive_link} onChange={(e) => upd("proton_drive_link", e.target.value)}
               placeholder="https://drive.proton.me/urls/…"
               data-testid="policy-proton" />
      </div>
      <div className="col-span-2 flex items-center gap-2">
        <input id="is_floater" type="checkbox" checked={!!form.is_floater}
               onChange={(e) => upd("is_floater", e.target.checked)}
               data-testid="policy-floater-toggle" />
        <label htmlFor="is_floater" className="text-xs">This is a floater (covers multiple members)</label>
      </div>
      {form.is_floater && (
        <div className="col-span-2">
          <FloaterMembersEditor
            members={form.floater_covered_members}
            onChange={(members) => upd("floater_covered_members", members)}
          />
        </div>
      )}
    </div>
  );
}

function FloaterMembersEditor({ members, onChange }) {
  const upd = (i, k, v) => {
    const next = [...members]; next[i] = { ...next[i], [k]: v }; onChange(next);
  };
  const add = () => onChange([...members, { name: "", relationship: "", linked_client_id: "" }]);
  const remove = (i) => onChange(members.filter((_, j) => j !== i));
  return (
    <div className="rounded-md border border-dashed border-border p-3">
      <div className="mb-2 flex items-center justify-between">
        <span className="text-xs font-semibold text-muted-foreground">Covered members</span>
        <Button size="sm" variant="outline" onClick={add} className="h-7 gap-1 text-[11px]"
                data-testid="add-floater-member">
          <Plus className="h-3 w-3" /> Add member
        </Button>
      </div>
      {members.length === 0 ? (
        <p className="text-[11px] text-muted-foreground">No members yet.</p>
      ) : (
        <div className="space-y-2">
          {members.map((m, i) => (
            <div key={i} className="grid grid-cols-7 gap-2" data-testid={`floater-row-${i}`}>
              <Input className="col-span-2" placeholder="Name" value={m.name || ""}
                     onChange={(e) => upd(i, "name", e.target.value)} />
              <Input className="col-span-2" placeholder="Relationship" value={m.relationship || ""}
                     onChange={(e) => upd(i, "relationship", e.target.value)} />
              <Input className="col-span-2" placeholder="Linked client id (optional)"
                     value={m.linked_client_id || ""}
                     onChange={(e) => upd(i, "linked_client_id", e.target.value)} />
              <Button variant="ghost" size="sm" onClick={() => remove(i)} className="h-10"
                      data-testid={`floater-remove-${i}`}>
                <Trash2 className="h-3 w-3" />
              </Button>
            </div>
          ))}
        </div>
      )}
      <p className="mt-2 text-[11px] text-muted-foreground">
        Set <b>linked client id</b> if a covered member is also a separate Client — their own
        Insurance section will then show a reference back to this policy instead of a duplicate entry.
      </p>
    </div>
  );
}

// ----------------------------------------------------------------------
// Detail dialog — full edit + renewal workflow.
// ----------------------------------------------------------------------
function PolicyDetailDialog({ open, onOpenChange, clientId, policyId, canAct, onChanged }) {
  const [policy, setPolicy] = useState(null);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    if (!policyId) return;
    try {
      const { data } = await api.get(`/clients/${clientId}/insurance-policies/${policyId}`);
      setPolicy(data);
      setForm({
        policy_type: data.policy_type || "Other",
        insurer: data.insurer || "",
        policy_number: data.policy_number || "",
        sum_insured: data.sum_insured ?? "",
        premium: data.premium ?? "",
        premium_frequency: data.premium_frequency || "",
        start_date: (data.start_date || "").slice(0, 10),
        expiry_date: (data.expiry_date || "").slice(0, 10),
        is_floater: !!data.is_floater,
        floater_covered_members: data.floater_covered_members || [],
        proton_drive_link: data.proton_drive_link || "",
        member_name: data.member_name || "",
      });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load policy.");
    }
  };
  useEffect(() => { if (open && policyId) load(); /* eslint-disable-next-line */ }, [open, policyId]);

  const save = async () => {
    if (!form) return;
    setSaving(true);
    try {
      await api.patch(`/clients/${clientId}/insurance-policies/${policyId}`, {
        ...form,
        sum_insured: form.sum_insured === "" ? null : Number(form.sum_insured),
        premium: form.premium === "" ? null : Number(form.premium),
      });
      toast.success("Policy updated.");
      onChanged && onChanged();
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed.");
    } finally { setSaving(false); }
  };

  const del = async () => {
    if (!window.confirm("Delete this policy? This cannot be undone.")) return;
    try {
      await api.delete(`/clients/${clientId}/insurance-policies/${policyId}`);
      toast.success("Policy deleted.");
      onChanged && onChanged();
      onOpenChange(false);
    } catch (e) { toast.error(e.response?.data?.detail || "Delete failed."); }
  };

  if (!form) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent><p className="text-sm text-muted-foreground">Loading…</p></DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="policy-detail-dialog">
        <DialogHeader>
          <DialogTitle>{form.insurer || "Policy"} · {form.policy_type}</DialogTitle>
          <DialogDescription>
            Full policy details, Proton Drive link, floater members, and renewal workflow.
          </DialogDescription>
        </DialogHeader>
        <PolicyFormFields form={form} setForm={setForm} />
        {canAct && (
          <div className="flex items-center justify-between pt-2">
            <Button variant="ghost" onClick={del} className="gap-1 text-destructive hover:text-destructive"
                    data-testid="policy-delete-btn">
              <Trash2 className="h-4 w-4" /> Delete
            </Button>
            <Button onClick={save} disabled={saving} data-testid="policy-update-btn">
              {saving ? "Saving…" : "Save changes"}
            </Button>
          </div>
        )}
        <RenewalPanel
          policy={policy}
          clientId={clientId}
          policyId={policyId}
          canAct={canAct}
          reload={load}
          onCommChanged={onChanged}
        />
        <RenewalHistoryLoader clientId={clientId} policy={policy} />
      </DialogContent>
    </Dialog>
  );
}

// Loads the client's documents once to display original filenames in the
// renewal-history footer rows. Kept small/local so the detail dialog stays
// uncluttered. If the docs fetch fails we silently render without filenames.
function RenewalHistoryLoader({ clientId, policy }) {
  const [docs, setDocs] = useState([]);
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/clients/${clientId}/documents`);
        setDocs(data || []);
      } catch { /* ignore */ }
    })();
  }, [clientId]);
  return <RenewalHistorySection policy={policy} docs={docs} />;
}

// ----------------------------------------------------------------------
// Renewal workflow panel inside the detail dialog.
// ----------------------------------------------------------------------
function RenewalPanel({ policy, clientId, policyId, canAct, reload, onCommChanged }) {
  const [docs, setDocs] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [linkDraft, setLinkDraft] = useState("");
  const [composerOpen, setComposerOpen] = useState(false);
  const [markRenewedOpen, setMarkRenewedOpen] = useState(false);
  useEffect(() => {
    if (!policy) return;
    setLinkDraft(policy.renewal_payment_link || "");
  }, [policy]);
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/clients/${clientId}/documents`);
        setDocs(data || []);
      } catch { /* ignore */ }
    })();
  }, [clientId]);

  const uploadQuote = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("doc_type", "Insurance Renewal Quote");
      const { data: uploaded } = await api.post(`/clients/${clientId}/documents`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      await api.post(`/clients/${clientId}/insurance-policies/${policyId}/renewal-quote`, {
        document_id: uploaded.id,
      });
      toast.success("Renewal quote attached.");
      await reload();
      // Refresh docs list for the dropdown
      const { data } = await api.get(`/clients/${clientId}/documents`);
      setDocs(data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed.");
    } finally { setUploading(false); }
  };

  const savePaymentLink = async () => {
    if (!linkDraft.trim()) { toast.error("Enter a payment URL first."); return; }
    try {
      await api.post(`/clients/${clientId}/insurance-policies/${policyId}/renewal-payment-link`, {
        renewal_payment_link: linkDraft.trim(),
      });
      toast.success("Payment link saved.");
      await reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed."); }
  };

  const setStatus = async (newStatus) => {
    try {
      await api.post(`/clients/${clientId}/insurance-policies/${policyId}/renewal-status`, {
        renewal_status: newStatus,
      });
      toast.success(`Status → ${newStatus}`);
      await reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Status change failed."); }
  };

  const hasQuote = !!policy?.renewal_quote_document_id;
  const hasLink = !!policy?.renewal_payment_link;
  const canSend = hasQuote && hasLink && canAct;
  const rs = policy?.renewal_status || "None";
  const quoteDoc = docs.find((d) => d.id === policy?.renewal_quote_document_id);

  return (
    <div className="mt-4 rounded-md border border-border bg-muted/30 p-4" data-testid="renewal-panel">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <h4 className="flex items-center gap-2 text-sm font-semibold">
          <CalendarClock className="h-4 w-4 text-primary" /> Renewal workflow
        </h4>
        <span className={"rounded-full border px-2 py-0.5 text-[10px] " + STATUS_TONE[rs]}
              data-testid="renewal-status-badge">{rs}</span>
      </div>

      <div className="grid gap-3 md:grid-cols-2">
        <div>
          <label className="mb-1 block text-xs font-semibold text-muted-foreground">1. Renewal quote</label>
          {quoteDoc ? (
            <div className="flex items-center justify-between rounded-md border border-border bg-white px-3 py-2 text-xs">
              <span className="truncate" title={quoteDoc.original_filename}>{quoteDoc.original_filename}</span>
              <span className="text-emerald-700">✓ Attached</span>
            </div>
          ) : (
            <p className="text-xs text-muted-foreground">No quote attached yet.</p>
          )}
          {canAct && (
            <label className="mt-2 inline-flex cursor-pointer items-center gap-1 text-xs text-primary">
              <Upload className="h-3 w-3" />
              <input type="file" className="hidden" accept=".pdf,.jpg,.jpeg,.png"
                     onChange={(e) => uploadQuote(e.target.files?.[0])}
                     data-testid="renewal-quote-upload" />
              {uploading ? "Uploading…" : (quoteDoc ? "Replace quote" : "Upload quote")}
            </label>
          )}
        </div>

        <div>
          <label className="mb-1 block text-xs font-semibold text-muted-foreground">2. Payment link</label>
          <div className="flex gap-2">
            <Input value={linkDraft} onChange={(e) => setLinkDraft(e.target.value)}
                   placeholder="https://pay.insurer.com/…"
                   disabled={!canAct}
                   data-testid="renewal-payment-link-input" />
            {canAct && (
              <Button size="sm" variant="outline" onClick={savePaymentLink}
                      data-testid="renewal-payment-link-save">
                Save
              </Button>
            )}
          </div>
          {hasLink && (
            <p className="mt-1 flex items-center gap-1 text-[11px] text-emerald-700">
              <LinkIcon className="h-3 w-3" /> Payment link saved
            </p>
          )}
        </div>
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-2">
        <Button
          size="sm"
          onClick={() => setComposerOpen(true)}
          disabled={!canSend}
          title={!canSend ? "Attach both the renewal quote AND the payment link first." : ""}
          className="gap-1"
          data-testid="renewal-send-btn"
        >
          <Send className="h-3 w-3" /> Send Renewal Email
        </Button>
        {!canSend && (
          <span className="flex items-center gap-1 text-[11px] text-amber-700">
            <AlertTriangle className="h-3 w-3" />
            Both quote and link must be present before sending.
          </span>
        )}
        {canAct && rs === "Renewal Email Sent" && (
          <Button size="sm" variant="outline" onClick={() => setMarkRenewedOpen(true)}
                  data-testid="renewal-mark-renewed">Mark Renewed</Button>
        )}
        {canAct && rs !== "Renewed" && rs !== "Lapsed" && (
          <Button size="sm" variant="ghost" onClick={() => setStatus("Lapsed")}
                  data-testid="renewal-mark-lapsed">Mark Lapsed</Button>
        )}
        {policy?.renewal_email_sent_at && (
          <span className="ml-auto text-[11px] text-muted-foreground">
            Last sent: {fmtDate(policy.renewal_email_sent_at)}
          </span>
        )}
      </div>

      <RenewalComposerDialog
        open={composerOpen}
        onOpenChange={setComposerOpen}
        clientId={clientId}
        policyId={policyId}
        policy={policy}
        onSent={() => { setComposerOpen(false); reload(); onCommChanged && onCommChanged(); }}
      />
      <MarkRenewedDialog
        open={markRenewedOpen}
        onOpenChange={setMarkRenewedOpen}
        clientId={clientId}
        policyId={policyId}
        policy={policy}
        onRolledOver={() => { setMarkRenewedOpen(false); reload(); }}
      />
    </div>
  );
}

// ----------------------------------------------------------------------
// Mark Renewed dialog — collects the NEW cycle's dates (required) and
// optional premium/policy #. On save the backend archives the old cycle
// into renewal_history, rolls active fields over, and resets the alert.
// ----------------------------------------------------------------------
function MarkRenewedDialog({ open, onOpenChange, clientId, policyId, policy, onRolledOver }) {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (open) {
      setForm({
        new_start_date: "",
        new_expiry_date: "",
        new_premium: policy?.premium ?? "",
        new_policy_number: policy?.policy_number || "",
      });
    }
  }, [open, policy]);

  const save = async () => {
    if (!form?.new_start_date || !form?.new_expiry_date) {
      toast.error("New start and expiry dates are required.");
      return;
    }
    if (form.new_expiry_date <= form.new_start_date) {
      toast.error("New expiry must be after the new start date.");
      return;
    }
    setSaving(true);
    try {
      await api.post(
        `/clients/${clientId}/insurance-policies/${policyId}/renewal-status`,
        {
          renewal_status: "Renewed",
          new_start_date: form.new_start_date,
          new_expiry_date: form.new_expiry_date,
          new_premium: form.new_premium === "" ? null : Number(form.new_premium),
          new_policy_number: form.new_policy_number || null,
        },
      );
      toast.success("Renewed — old cycle archived, new cycle active.");
      onRolledOver && onRolledOver();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not complete renewal.");
    } finally { setSaving(false); }
  };

  if (!form) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="mark-renewed-dialog">
        <DialogHeader>
          <DialogTitle>Mark policy as renewed</DialogTitle>
          <DialogDescription>
            Enter what the <b>new</b> policy document actually says — not a system guess. The old
            cycle (quote, link, dates, premium) will be archived in the renewal history.
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="mb-1 block text-xs font-semibold text-muted-foreground">New start date *</label>
            <Input type="date" value={form.new_start_date}
                   onChange={(e) => setForm({ ...form, new_start_date: e.target.value })}
                   data-testid="mark-renewed-start" />
          </div>
          <div>
            <label className="mb-1 block text-xs font-semibold text-muted-foreground">New expiry date *</label>
            <Input type="date" value={form.new_expiry_date}
                   onChange={(e) => setForm({ ...form, new_expiry_date: e.target.value })}
                   data-testid="mark-renewed-expiry" />
          </div>
          <div>
            <label className="mb-1 block text-xs font-semibold text-muted-foreground">New premium (₹)</label>
            <Input type="number" value={form.new_premium}
                   onChange={(e) => setForm({ ...form, new_premium: e.target.value })}
                   data-testid="mark-renewed-premium" />
          </div>
          <div>
            <label className="mb-1 block text-xs font-semibold text-muted-foreground">New policy #</label>
            <Input value={form.new_policy_number}
                   onChange={(e) => setForm({ ...form, new_policy_number: e.target.value })}
                   data-testid="mark-renewed-policyno" />
          </div>
        </div>
        <p className="text-[11px] text-muted-foreground">
          The 2-month expiry reminder resets automatically — it'll fire again two months before the
          new expiry date you just entered, whenever that is.
        </p>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="mark-renewed-save">
            {saving ? "Saving…" : "Confirm renewal"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ----------------------------------------------------------------------
// Renewal history — collapsed section listing past cycles.
// ----------------------------------------------------------------------
function RenewalHistorySection({ policy, docs }) {
  const [open, setOpen] = useState(false);
  const history = policy?.renewal_history || [];
  if (history.length === 0) return null;
  return (
    <div className="mt-4 rounded-md border border-border p-4" data-testid="renewal-history">
      <button
        className="flex w-full items-center justify-between text-left"
        onClick={() => setOpen(!open)}
        data-testid="renewal-history-toggle"
      >
        <span className="flex items-center gap-2 text-sm font-semibold">
          <CalendarClock className="h-4 w-4 text-primary" />
          Renewal history
          <Badge variant="secondary" className="text-[10px]">{history.length}</Badge>
        </span>
        <span className="text-xs text-muted-foreground">{open ? "Hide" : "Show"}</span>
      </button>
      {open && (
        <ol className="mt-3 space-y-2">
          {history.map((h, i) => {
            const quote = docs.find((d) => d.id === h.renewal_quote_document_id);
            return (
              <li key={i} className="rounded-md border border-dashed border-border bg-muted/30 p-3 text-xs"
                  data-testid={`renewal-history-row-${i}`}>
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-semibold">Cycle {i + 1}</span>
                  <span className="text-muted-foreground">
                    {fmtDate(h.start_date)} → {fmtDate(h.expiry_date)}
                  </span>
                  <span className="text-muted-foreground">· {fmtCurrency(h.premium)}</span>
                  {h.policy_number && <span className="text-muted-foreground">· #{h.policy_number}</span>}
                </div>
                <div className="mt-1 text-[11px] text-muted-foreground">
                  Renewed on {fmtDate(h.renewed_on)}
                  {quote && <> · <span className="italic">{quote.original_filename}</span></>}
                  {h.renewal_payment_link && (
                    <> · <a href={h.renewal_payment_link} target="_blank" rel="noreferrer"
                            className="text-primary hover:underline">payment link</a></>
                  )}
                  {h.renewal_email_sent_at && <> · email sent {fmtDate(h.renewal_email_sent_at)}</>}
                </div>
              </li>
            );
          })}
        </ol>
      )}
    </div>
  );
}

function RenewalComposerDialog({ open, onOpenChange, clientId, policyId, policy, onSent }) {
  const [step, setStep] = useState("input");
  const [adminInput, setAdminInput] = useState("");
  const [draft, setDraft] = useState("");
  const [aiDraft, setAiDraft] = useState("");
  const [drafting, setDrafting] = useState(false);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (open) {
      setStep("input");
      setAdminInput("");
      setDraft("");
      setAiDraft("");
    }
  }, [open]);

  const runDraft = async () => {
    if (!adminInput.trim()) { toast.error("Type a short note first."); return; }
    setDrafting(true);
    try {
      const { data } = await api.post(
        `/clients/${clientId}/insurance-policies/${policyId}/renewal-draft`,
        { admin_input_message: adminInput },
      );
      setDraft(data.draft || "");
      setAiDraft(data.draft || "");
      setStep("review");
    } catch (e) { toast.error(e.response?.data?.detail || "Draft failed."); }
    finally { setDrafting(false); }
  };

  const send = async () => {
    if (!draft.trim()) { toast.error("Draft is empty."); return; }
    setSending(true);
    try {
      await api.post(
        `/clients/${clientId}/insurance-policies/${policyId}/renewal-send`,
        { admin_input_message: adminInput, ai_generated_draft: aiDraft, final_sent_text: draft },
      );
      toast.success("Renewal email sent — logged in the Communication tab.");
      onSent && onSent();
    } catch (e) { toast.error(e.response?.data?.detail || "Send failed."); }
    finally { setSending(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="renewal-composer">
        <DialogHeader>
          <DialogTitle>Send renewal email · {policy?.insurer}</DialogTitle>
          <DialogDescription>
            {step === "input"
              ? "Short note on what to tell the client about this renewal. AI will turn it into a polished, branded email with the quote attached and the payment link embedded."
              : "Review the AI-generated draft. Edit anything, then send."}
          </DialogDescription>
        </DialogHeader>
        {step === "input" ? (
          <>
            <Textarea rows={6} value={adminInput} onChange={(e) => setAdminInput(e.target.value)}
                      placeholder="e.g. premium is roughly the same as last year, flag that the SI can be bumped by 25%…"
                      data-testid="renewal-admin-input" />
            <DialogFooter>
              <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
              <Button onClick={runDraft} disabled={drafting} className="gap-1" data-testid="renewal-draft-btn">
                <Sparkles className="h-4 w-4" /> {drafting ? "Drafting…" : "Draft with AI"}
              </Button>
            </DialogFooter>
          </>
        ) : (
          <>
            <div className="rounded-md border border-dashed border-border bg-muted/30 p-3 text-xs">
              <p className="mb-1 font-semibold text-muted-foreground">Your input</p>
              <p className="whitespace-pre-line text-foreground/80">{adminInput}</p>
            </div>
            <div>
              <p className="mb-1 text-xs font-semibold text-muted-foreground">Draft — edit as needed</p>
              <Textarea rows={14} value={draft} onChange={(e) => setDraft(e.target.value)}
                        className="font-sans text-sm leading-relaxed" data-testid="renewal-draft-body" />
              <p className="mt-1 text-[11px] text-muted-foreground">
                We'll wrap this in the Reliserve Wealth template, attach the renewal quote, embed the payment link, and CC contact@.
              </p>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setStep("input")}>Back</Button>
              <Button onClick={send} disabled={sending} className="gap-1" data-testid="renewal-send-confirm">
                <Send className="h-4 w-4" /> {sending ? "Sending…" : "Send renewal email"}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
